import React, { Component } from "react";
import { Row } from "react-bootstrap";
import { organizationApiService } from "../../../services/apiV2Service";
import { CompanyRegulatoryDocuments } from "./CompanyRegulatoryDocuments.jsx";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";
import { useCountries } from "../../../hooks/useCountries";
import { inputValidationService } from "./services/InputValidationService";

export class CompanyProfile extends Component {
  constructor() {
    super();
    this.state = {
      logoUrl: "",
      name: "",
      address: "",
      email: "",
      phoneNo: "",
      domain: "",
      webUrl: "",
      isEnabled: "",
      country: "",
      countryId: null,
      originalCountryId: null,
      type: "",
      spaceType: "LOW",
      latitude: "",
      longitude: "",
      streetName: "",
      isNameInvalid: false,
      isAddressInvalid: false,
      isEmailInvalid: false,
      isPhoneNoInvalid: false,
      isDomainInvalid: false,
      isWebUrlInvalid: false,
      isCountryInvalid: false,
      showSuccessModal: false,
      hasLogo: false,
      supportedCountries: [],
      loading: true,
      error: "",
      saving: false,
      savingCountry: false,
      success: false,
      countryUpdateSuccess: false,
    };
  }

  componentDidMount = () => {
    this.fetchOrganizationDetails();
    this.fetchCountries();
  };

  componentWillUnmount = () => {
    // Cleanup if needed
  };

  currentUserDetails = () => {
    let userDetails = {};
    const jsonStr = localStorage.getItem("currentUser");

    if (jsonStr !== undefined) {
      userDetails = JSON.parse(jsonStr);
    }

    return userDetails;
  };

  fetchOrganizationDetails = async () => {
    try {
      this.setState({ loading: true, error: "" });

      // Use currentUser prop if available, otherwise fall back to localStorage
      const currentUserDetails = this.props.currentUser || this.currentUserDetails();
      const organizationId = currentUserDetails?.organizationId || currentUserDetails?.companyId;

      if (!organizationId) {
        this.setState({
          loading: false,
          error: "No organization ID found in user profile",
        });
        return;
      }

      logger.log("Fetching organization details for ID:", organizationId);
      const response = await organizationApiService.getOrganizationById(organizationId);

      if (response.status === 200) {
        const orgData = response.data?.data || response.data;
        logger.log("Organization data received:", orgData);

        // Format phone number for PhoneInput component
        let formattedPhoneNo = orgData.contactMsisdn || "";
        if (formattedPhoneNo && !formattedPhoneNo.startsWith("+")) {
          // Add + prefix if not present
          formattedPhoneNo = `+${formattedPhoneNo}`;
        }

        this.setState({
          name: orgData.name || "",
          address: orgData.address || "",
          email: orgData.contactEmail || "",
          phoneNo: formattedPhoneNo,
          domain: orgData.domain || "",
          webUrl: orgData.webUrl || "",
          isEnabled: orgData.status === "ACTIVE",
          country: orgData.country?.code || "",
          countryId: orgData.country?.id || orgData.countryId || null,
          originalCountryId: orgData.country?.id || orgData.countryId || null,
          type: orgData.type?.name || "",
          spaceType: orgData.spaceType || "LOW",
          logoUrl: orgData.logoUrl || "",
          hasLogo: !!orgData.logoUrl,
          loading: false,
          error: "",
        });
      } else {
        this.setState({
          loading: false,
          error: "Failed to load organization details",
        });
        showApiMessage(response, "load organization");
      }
    } catch (error) {
      logger.error("Error fetching organization details:", error);
      this.setState({
        loading: false,
        error: "Failed to load organization details",
      });
      showApiMessage(error, "load organization");
    }
  };

  fetchCountries = async () => {
    // Countries are now provided via props from useCountries hook
    const { countries } = this.props;
    if (countries && countries.length > 0) {
      this.setState({
        supportedCountries: countries,
      });
      logger.log("Countries loaded from cache:", countries);
    }
  };

  onSubmit = async event => {
    event.preventDefault();

    const { name, address, email, phoneNo, domain, webUrl, countryId } = this.state;

    // Validation
    const isNameInvalid = !name || name.trim() === "";
    const isAddressInvalid = !address || address.trim() === "";
    const isEmailInvalid = !email || !inputValidationService.validateEmail(email);
    const isPhoneNoInvalid = !phoneNo || !isValidPhoneNumber(phoneNo);
    const isDomainInvalid = !domain || domain.trim() === "";
    const isWebUrlInvalid = !webUrl || webUrl.trim() === "";
    const isCountryInvalid = !countryId;

    if (
      isNameInvalid ||
      isAddressInvalid ||
      isEmailInvalid ||
      isPhoneNoInvalid ||
      isDomainInvalid ||
      isWebUrlInvalid ||
      isCountryInvalid
    ) {
      this.setState({
        isNameInvalid,
        isAddressInvalid,
        isEmailInvalid,
        isPhoneNoInvalid,
        isDomainInvalid,
        isWebUrlInvalid,
        isCountryInvalid,
      });
      this.setState({ saving: false });
      return;
    }

    try {
      this.setState({
        saving: true,
        error: "",
        success: false,
        isNameInvalid: false,
        isAddressInvalid: false,
        isEmailInvalid: false,
        isPhoneNoInvalid: false,
        isDomainInvalid: false,
        isWebUrlInvalid: false,
        isCountryInvalid: false,
      });

      // Use currentUser prop if available, otherwise fall back to localStorage
      const currentUserDetails = this.props.currentUser || this.currentUserDetails();
      const organizationId = currentUserDetails?.organizationId || currentUserDetails?.companyId;

      if (!organizationId) {
        this.setState({
          saving: false,
          error: "No organization ID found in user profile",
        });
        return;
      }

      // Format phone number for API (remove + prefix if present)
      let formattedPhoneForApi = phoneNo || "";
      if (formattedPhoneForApi && formattedPhoneForApi.startsWith("+")) {
        formattedPhoneForApi = formattedPhoneForApi.substring(1);
      }

      const updatePayload = {
        name: name,
        address: address || "",
        contactEmail: email || "",
        contactMsisdn: formattedPhoneForApi,
        domain: domain || "",
        webUrl: webUrl || "",
      };

      logger.log("Updating organization with payload:", updatePayload);
      const response = await organizationApiService.updateSelfOrganization(updatePayload);

      if (response.status === 200) {
        this.setState({
          saving: false,
          success: true,
          error: "",
        });
        showApiMessage(response, "update organization");
      } else {
        this.setState({
          saving: false,
          error: "Failed to update organization",
        });
        showApiMessage(response, "update organization");
      }
    } catch (error) {
      logger.error("Error updating organization:", error);
      this.setState({
        saving: false,
        error: "Failed to update organization",
      });
      showApiMessage(error, "update organization");
    }
  };

  handleChange = e => {
    const name = e.target.name;
    const value = e.target.value;

    this.setState({
      [name]: value,
      // Reset any validation states
      isNameInvalid: false,
      isAddressInvalid: false,
      isEmailInvalid: false,
      isPhoneNoInvalid: false,
      isDomainInvalid: false,
      isWebUrlInvalid: false,
      isCountryInvalid: false,
    });
  };

  handleCountryChange = async e => {
    const selectedCountryId = parseInt(e.target.value);
    const { supportedCountries } = this.state;

    // Find the selected country object
    const selectedCountry = supportedCountries.find(c => c.id === selectedCountryId);

    if (!selectedCountry) {
      logger.error("Selected country not found in supported countries");
      return;
    }

    // Update local state immediately for responsive UI
    this.setState({
      countryId: selectedCountryId,
      country: selectedCountry.code,
      isCountryInvalid: false,
    });

    // Call the API to update the organization's country
    try {
      this.setState({ savingCountry: true, countryUpdateSuccess: false });

      const currentUserDetails = this.props.currentUser || this.currentUserDetails();
      const organizationId = currentUserDetails?.organizationId || currentUserDetails?.companyId;

      if (!organizationId) {
        this.setState({
          savingCountry: false,
          error: "No organization ID found in user profile",
        });
        return;
      }

      logger.log("Updating organization country to:", selectedCountryId);
      const response = await organizationApiService.updateOrganizationCountry(organizationId, selectedCountryId);

      if (response.status === 200) {
        logger.log("Country updated successfully:", response);
        this.setState({
          savingCountry: false,
          countryUpdateSuccess: true,
          originalCountryId: selectedCountryId,
        });

        // Show success message
        const syntheticSuccess = {
          status: 200,
          data: {
            message: `Country updated to ${selectedCountry.name || selectedCountry.officialName}`,
          },
        };
        showApiMessage(syntheticSuccess, "update country");

        // Trigger currency change event for other components
        window.dispatchEvent(new Event("currencyChanged"));
      } else {
        // Revert to original country on failure
        this.setState(prevState => ({
          savingCountry: false,
          countryId: prevState.originalCountryId,
          country: supportedCountries.find(c => c.id === prevState.originalCountryId)?.code || "",
        }));
        showApiMessage(response, "update country");
      }
    } catch (error) {
      logger.error("Error updating country:", error);
      // Revert to original country on error
      this.setState(prevState => ({
        savingCountry: false,
        countryId: prevState.originalCountryId,
        country: supportedCountries.find(c => c.id === prevState.originalCountryId)?.code || "",
      }));
      showApiMessage(error, "update country");
    }
  };

  render() {
    const {
      logoUrl: _logoUrl,
      name,
      address,
      email,
      phoneNo,
      isNameInvalid,
      isAddressInvalid,
      isEmailInvalid,
      isPhoneNoInvalid,
      isDomainInvalid,
      isWebUrlInvalid,
      isCountryInvalid,
      hasLogo: _hasLogo,
      domain,
      webUrl,
      country: _country,
      spaceType: _spaceType,
      supportedCountries,
      loading,
      error,
      saving,
      success: _success,
    } = this.state;

    if (loading) {
      return (
        <div className="company-profile-container">
          <h4>Company Profile</h4>
          <div className="flex justify-center items-center h-32">
            <div className="w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
            <span className="ml-2 text-gray-600">Loading organization details...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="company-profile-container">
          <h4>Company Profile</h4>
          <div className="py-8 text-center text-red-600 bg-red-50 rounded-lg">
            {error}
            <button
              onClick={this.fetchOrganizationDetails}
              className="block mx-auto mt-4 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={"company-profile-container"}>
        <h4>Company Profile</h4>
        {/* <div className="ellipse">
                <img src={ hasLogo ? logoUrl : defaultCompanyLogo } alt={""} className="ellipse" />
                <FaCamera className="ellipseAttribute" color="blue" />
            </div> */}
        <form onSubmit={this.onSubmit}>
          <br />
          <Row>
            <div className="form-group col">
              <label htmlFor="">Name</label>
              <input
                type="text"
                className={isNameInvalid ? "form-control is-invalid" : "form-control"}
                defaultValue={name}
                onChange={this.handleChange}
                name="name"
                id=""
                aria-describedby="emailHelpId"
                placeholder=""
              />
              <div className="invalid-feedback">Your company name is required</div>
            </div>
            <br />
          </Row>
          <Row>
            <div className="form-group col">
              <label htmlFor="">Phone Number</label>
              <div className="phone-input-wrapper">
                <PhoneInput
                  international
                  limitMaxLength={true}
                  defaultCountry="KE"
                  value={phoneNo}
                  onChange={value => {
                    const isInvalid = value && !isValidPhoneNumber(value);
                    this.setState({ phoneNo: value, isPhoneNoInvalid: isInvalid });
                  }}
                  className={`phone-input ${isPhoneNoInvalid ? "phone-input-error" : ""}`}
                  inputProps={{ maxLength: 16 }}
                />
              </div>
              {isPhoneNoInvalid && (
                <div className="text-red-500 text-xs mt-1">Your company phone number is required and must be valid</div>
              )}
            </div>
            <br />
            <div className="form-group col">
              <label htmlFor="">E-mail</label>
              <input
                type="text"
                className={isEmailInvalid ? "form-control is-invalid" : "form-control"}
                defaultValue={email}
                onChange={this.handleChange}
                name="email"
                id=""
                aria-describedby="emailHelpId"
                placeholder=""
              />
              <div className="invalid-feedback">Your company e-mail is required</div>
            </div>
            <br />
          </Row>

          <Row>
            <div className="form-group col">
              <label htmlFor="">Domain </label>
              <input
                type="text"
                className={isDomainInvalid ? "form-control is-invalid" : "form-control"}
                defaultValue={domain}
                onChange={this.handleChange}
                name="domain"
                id=""
                aria-describedby="emailHelpId"
                placeholder=""
              />
              <div className="invalid-feedback">Your company internet domain name is required</div>
            </div>
            <br />
            <div className="form-group col">
              <label htmlFor="">Website Url </label>
              <input
                type="text"
                className={isWebUrlInvalid ? "form-control is-invalid" : "form-control"}
                defaultValue={webUrl}
                onChange={this.handleChange}
                name="webUrl"
                id=""
                aria-describedby="emailHelpId"
                placeholder=""
              />
              <div className="invalid-feedback">Your company website URL is required</div>
            </div>
            <br />
          </Row>

          <Row>
            <div className="form-group col">
              <label htmlFor="">Address</label>
              <textarea
                className={isAddressInvalid ? "form-control is-invalid" : "form-control"}
                defaultValue={address}
                onChange={this.handleChange}
                name="address"
                id=""
                aria-describedby="emailHelpId"
                placeholder=""
              ></textarea>
              <div className="invalid-feedback">Your company address is required</div>
            </div>
          </Row>

          <Row>
            <div className="form-group col">
              <label htmlFor="">Country</label>
              <div className="relative">
                <select
                  className={isCountryInvalid ? "form-control is-invalid" : "form-control"}
                  name="countryId"
                  value={this.state.countryId || ""}
                  onChange={this.handleCountryChange}
                  disabled={this.state.savingCountry}
                >
                  <option disabled value={""}>
                    Select operating country
                  </option>
                  {supportedCountries.map(country => {
                    const countryName = country?.officialName || country?.name;

                    return (
                      <option key={country?.id} value={country?.id}>
                        {countryName}
                      </option>
                    );
                  })}
                </select>
                {this.state.savingCountry && (
                  <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                  </div>
                )}
              </div>
              {this.state.countryUpdateSuccess && (
                <small className="text-green-600 mt-1">Country updated successfully</small>
              )}
              <div className="invalid-feedback">Your company operating country is required</div>
            </div>
            <br />

            <div className="form-group col">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
            {/*    <div className="form-group col">
                        <label htmlFor="">Listing category</label>
                        <select className='form-control' value={spaceType} name="spaceType" onChange={  this.handleChange } >
                            <option selected disabled value={ "" }>Select your listing category</option>
                            <option value="low">Enterprise</option>
                            <option value="medium">Corporate</option>
                            <option value="high">Corporate Plus</option>
                        </select>
                    </div> */}
            <br />
            {/*</div>*/}
          </Row>
          <br />

          {/* <h6>TEST</h6> */}
          <CompanyRegulatoryDocuments />

          <div className="flex gap-2 justify-end">
            <button type="submit" className="button" style={{ width: "20%" }} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
          <br />
        </form>
        {/* 
            <Modal show={this.state.showSuccessModal} onHide={this.handleStuffOnModalHide} onEnter={e => logger.log(e)}  style={{
                    overflow: "hidden",
                    borderRadius: "30px",
                }}>
                 <Modal.Header
                    style={{
                        borderBottom: "none",
                        paddingLeft: "30px",
                        paddingRight: "30px",
                        margin: "0px",
                    }}
                    closeButton
                >
                <div className=""></div>
                </Modal.Header>
                <Modal.Body style={{height: '310px'}}>
                    <div className="text-center position-relative" style={{bottom: '55px'}}>
                        <figure>
                            <img
                                className={'position-relative'}
                                src={successImage}
                                alt="Finish Setup Illustration"
                                style={{ width: '40%' }}
                            />
                        </figure>
                        <p className={'text-primary font-weight-bolder mb-2'} style={{ fontSize: '1.03rem' }}>Submitted Successfully</p>
                        <p style={{ fontSize: '0.89rem' }}>
                            We have received your documents and will get back to you within 2-3 business days
                        </p>
                        <div className={'mt-4'}>
                            <button
                                className={'btn btn-primary text-white'}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    this.setState({
                                        showSuccessModal: false
                                    })
                                }
                                }
                            >
                                Resubmit
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal> */}
        <style jsx global>{`
          .phone-input-wrapper .PhoneInput {
            background-color: white !important;
            border: 1px solid #d1d5db !important;
            border-radius: 0.375rem !important;
            padding: 0.5rem 0.75rem !important;
            font-size: 0.875rem !important;
            transition:
              border-color 0.15s ease-in-out,
              box-shadow 0.15s ease-in-out !important;
            display: flex !important;
            align-items: center !important;
          }

          .phone-input-wrapper .PhoneInput:focus-within {
            outline: none !important;
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }

          .phone-input-wrapper .PhoneInput.phone-input-error {
            border-color: #ef4444 !important;
          }

          .phone-input-wrapper .PhoneInput.phone-input-error:focus-within {
            border-color: #ef4444 !important;
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
          }

          .phone-input-wrapper .PhoneInputInput {
            background-color: transparent !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            font-size: 0.875rem !important;
            color: #111827 !important;
            outline: none !important;
            flex: 1 !important;
          }

          .phone-input-wrapper .PhoneInputInput::placeholder {
            color: #9ca3af !important;
          }

          .phone-input-wrapper .PhoneInputCountry {
            margin-right: 0.5rem !important;
            padding: 0 !important;
            border: none !important;
            background: transparent !important;
            display: flex !important;
            align-items: center !important;
            flex-shrink: 0 !important;
          }

          .phone-input-wrapper .PhoneInputCountryIcon {
            margin-right: 0.25rem !important;
            width: 1.25rem !important;
            height: auto !important;
            display: block !important;
          }

          .phone-input-wrapper .PhoneInputCountryIcon img {
            width: 1.25rem !important;
            height: auto !important;
            display: block !important;
          }

          .phone-input-wrapper .PhoneInputCountrySelect {
            color: #111827 !important;
            font-size: 0.875rem !important;
            background: transparent !important;
            border: none !important;
            outline: none !important;
            cursor: pointer !important;
          }

          .phone-input-wrapper .PhoneInputCountrySelectArrow {
            color: #6b7280 !important;
            margin-left: 0.25rem !important;
          }
        `}</style>
      </div>
    );
  }
}

// Wrapper component to use useCountries hook
const CompanyProfileWrapper = props => {
  const { countries, countriesLoading } = useCountries();

  return <CompanyProfile {...props} countries={countries} countriesLoading={countriesLoading} />;
};

export default CompanyProfileWrapper;
